import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomMessageService, RequestDataService, OriginMessageError } from "library";

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptor implements HttpInterceptor {

  // Variable global para almacenar el último requestData
  public static lastRequestData: any = null;

  constructor(
    private router: Router,
    private customMessageService: CustomMessageService,
    private requestDataService: RequestDataService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Generar traceFrontendId usando el servicio
    const traceFrontendId = this.requestDataService.generateTraceFrontendId();

    // Capturar datos de la request con el ID
    const requestData = {
      ...this.buildRequestData(req),
      traceFrontendId,
      timestamp: new Date().toISOString()
    };

    // Guardar en el service con el ID
    this.requestDataService.setRequestData(traceFrontendId, requestData);

    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        // Agregar traceFrontendId al BODY de las respuestas exitosas
        if (event instanceof HttpResponse) {
          const modifiedBody = {
            ...event.body,
            traceFrontendId: traceFrontendId
          };

          return event.clone({
            body: modifiedBody
          });
        }
        return event;
      }),
      catchError((errorRes) => {
        // Mantener el traceFrontendId en errores
        errorRes.traceFrontendId = traceFrontendId;
        return this.handleHttpError(errorRes, requestData, req);
      })
    );
  }

  /**
   * Maneja los diferentes tipos de errores HTTP
   */
  private handleHttpError(errorRes: any, requestData: any, req: HttpRequest<any>): Observable<never> {
    // console.warn('HTTP Error:', errorRes);

    switch (errorRes.status) {
      case HttpStatusCode.Unauthorized:
        return this.handleUnauthorizedError(errorRes, req);

      case HttpStatusCode.FailedDependency:
        return this.handleFailedDependencyError(errorRes, requestData);

      default:
        return throwError(errorRes);
    }
  }

  /**
   * Maneja errores de autenticación (401)
   */
  private handleUnauthorizedError(errorRes: any, req: HttpRequest<any>): Observable<never> {
    console.warn(errorRes);
    const isAuthEndpoint = req.url.includes('/auth/check-credentials') ||
      req.url.includes('/auth/sign-in');

    if (isAuthEndpoint) {
      // Propagar el error al componente para que lo maneje
      return throwError(errorRes);
    } else {
      // Error de sesión expirada (durante uso de la app)
      this.router.navigate(['/logout']);
      let message = "Puede que tu sesión siga activa en otro navegador. Revisa si tienes otra sesión abierta y valida tu usuario y clave antes de volver a intentar";
      this.customMessageService.error(message);
      return throwError({...errorRes, message: message + "." + errorRes.error.error +': '+ errorRes.message});
    }
  }

  /**
   * Maneja errores de dependencia fallida (424)
   */
  private handleFailedDependencyError(errorRes: any, requestData: any): Observable<never> {
    this.customMessageService.responseNoticeHandler(errorRes);
    return of([]) as any;
  }

  /**
   * Construye objeto con datos de la request para logging
   */
  private buildRequestData(req: HttpRequest<any>): any {
    return {
      url: req.url,
      method: req.method,
      headers: this.extractHeaders(req.headers),
      body: req.body
    };
  }

  /**
   * Extrae headers del HttpHeaders a un objeto plano
   */
  private extractHeaders(headers: any): any {
    const headerObj: any = {};

    if (headers?.keys) {
      headers.keys().forEach((key: string) => {
        headerObj[key] = headers.get(key);
      });
    }

    return headerObj;
  }
}
